import {
  PaymentRequestForm,
  TransactionProperty,
} from '@/features/InvoiceReport/Edit/type';
import { useModelsToOptionsWithData } from '@/hooks/useTypeToOptions';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  FormSelect,
  SelectProps,
  Skeleton,
} from '@moneyforward/ap-frontend-components';
import { ApGeneralInputOption } from 'ap-openapi';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styles from './Pulldown.module.scss';
import { usePulldownOption, usePulldownOptions } from './usePulldownOptions';

type Props = {
  index: number;
  property: TransactionProperty;
};

const cx = classnames.bind(styles);

export const PulldownType: FC<Props> = memo(({ index, property }) => {
  const { control } = useFormContext<PaymentRequestForm>();
  const containerRef = useGlobalContainerRef();

  const {
    optionList,
    isLoading,
    error,
    onSearch,
    fetchMore,
    handleDropdownVisibleChange,
    query,
  } = usePulldownOptions({
    propertyId: property.id,
    per: 25,
  });

  if (error) throw error;

  const optionIdValue = useWatch({
    control,
    name: `invoiceTransactions.${index}.custom_transaction_items.${property.id}` as const,
  });

  const optionId = optionIdValue ? String(optionIdValue) : undefined;

  const {
    defaultOption,
    isLoading: isLoadingDefaultOption,
    error: errorDefaultOption,
  } = usePulldownOption({
    id: optionId,
    propertyId: property.id,
  });

  const memoizedDefaultOption = useMemo(
    () => (isLoading ? [] : defaultOption),
    [isLoading, defaultOption]
  );

  if (errorDefaultOption) throw errorDefaultOption;

  const options = useModelsToOptionsWithData<ApGeneralInputOption>(
    optionList,
    query,
    memoizedDefaultOption,
    {
      labelKeys: ['label'],
      caption: 'code',
    },
    true
  );

  const selectProps: SelectProps = useMemo(
    () => ({
      options: options,
      loading: isLoading,
      mode: 'single',
      onDropdownVisibleChange: handleDropdownVisibleChange,
      onSearch: onSearch,
      fetchMore: fetchMore,
      onSearchDebounceWait: 600,
      getPopupContainer: containerRef,
      comboBox: true,
      placeholder: property.placeholder,
    }),
    [
      options,
      isLoading,
      handleDropdownVisibleChange,
      onSearch,
      fetchMore,
      containerRef,
      property.placeholder,
    ]
  );

  return (
    <>
      {isLoadingDefaultOption ? (
        <div className={cx(styles['loading-container'])}>
          <Skeleton.Input active size='default' block />
        </div>
      ) : (
        <FormSelect<PaymentRequestForm>
          name={`invoiceTransactions.${index}.custom_transaction_items.${property.id}`}
          control={control}
          inputId={`${property.id}${index}`}
          selectProps={selectProps}
        />
      )}
    </>
  );
});
PulldownType.displayName = 'PulldownType';
